import HomeView from 'presentation/view/home/HomeView'
import React, { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router-dom'
import { LoadingSpinner } from 'presentation/shared/LoadingSpinner'

const AdminGuard = lazy(() => import('presentation/view/admin/AdminGuard'))
const AdminLayout = lazy(() => import('presentation/view/admin/AdminLayout'))
const AdminRestaurantsView = lazy(() => import('presentation/view/admin/restaurants/AdminRestaurantsView'))
const AdminRestaurantDetailView = lazy(() => import('presentation/view/admin/restaurants/AdminRestaurantDetailView'))
const AdminFoodsView = lazy(() => import('presentation/view/admin/foods/AdminFoodsView'))
const AdminFeedbacksView = lazy(() => import('presentation/view/admin/feedbacks/AdminFeedbacksView'))
const AdminAgentRunsView = lazy(() => import('presentation/view/admin/agent-runs/AdminAgentRunsView'))
const AdminRecognitionView = lazy(() => import('presentation/view/admin/recognition/AdminRecognitionView'))
const AdminAnnotationsView = lazy(() => import('presentation/view/admin/annotations/AdminAnnotationsView'))
const AdminArchiveView = lazy(() => import('presentation/view/admin/archive/AdminArchiveView'))
const AdminMenuDraftView = lazy(() => import('presentation/view/admin/menus/AdminMenuDraftView'))
import DynamicIsland from 'presentation/shared/DynamicIsland'
import './index.css'
import './i18n/config'
import './lib/firebase'
import moment from 'moment'
import 'moment/locale/ru'
import { ThemeProvider } from 'presentation/shared/ThemeProvider'
import { TooltipProvider } from 'components/ui/tooltip'
import PrivatePolicyView from 'presentation/view/private/PrivatePolicy'
import HelpAccountDeleteView from 'presentation/view/help/account/HelpAccountDelete'
import MobileStoreRedirectView from 'presentation/view/mobilestore/MobileStoreRedirectView'
import LoginScreen from './presentation/screen/LoginScreen'
import OAuthCallbackScreen from './presentation/screen/OAuthCallbackScreen'
import OTTCallbackScreen from './presentation/screen/OTTCallbackScreen'
import { AppProvider } from './context'

moment().locale('ru')

function RootLayout() {
	const location = useLocation()
	const hideNavbar = new URLSearchParams(location.search).get('hideNavbar') === 'true'

	return (
		<>
			{!hideNavbar && <DynamicIsland />}
			<Outlet />
		</>
	)
}

const router = createBrowserRouter([
	{
		path: '/admin',
		element: (
			<Suspense
				fallback={
					<div className='fixed inset-0 flex items-center justify-center'>
						<LoadingSpinner />
					</div>
				}>
				<AdminGuard>
					<AdminLayout />
				</AdminGuard>
			</Suspense>
		),
		children: [
			{ index: true, element: <Navigate to='/admin/restaurants' replace /> },
			{ path: 'restaurants', element: <AdminRestaurantsView /> },
			{ path: 'restaurants/:id', element: <AdminRestaurantDetailView /> },
			{ path: 'foods', element: <AdminFoodsView /> },
			{ path: 'feedbacks', element: <AdminFeedbacksView /> },
			{ path: 'annotations', element: <AdminAnnotationsView /> },
			{ path: 'recognition', element: <AdminRecognitionView /> },
			{ path: 'agent-runs', element: <AdminAgentRunsView /> },
			{ path: 'agent-runs/:id', element: <AdminAgentRunsView /> },
			{ path: 'archive', element: <AdminArchiveView /> },
			{ path: 'menus/:id', element: <AdminMenuDraftView /> },
		],
	},
	{
		element: <RootLayout />,
		children: [
			{
				path: '/',
				element: <HomeView />,
			},
			{
				path: '/login',
				element: <LoginScreen />,
			},
			{
				path: '/oauth/callback',
				element: <OAuthCallbackScreen />,
			},
			{
				path: '/oauth/ott',
				element: <OTTCallbackScreen />,
			},
			{
				path: '/app',
				element: <MobileStoreRedirectView />,
			},
			{
				path: '/privacy',
				element: <PrivatePolicyView />,
			},
			{
				path: '/privacy/ru',
				element: <PrivatePolicyView />,
			},
			{
				path: '/privacy/en',
				element: <PrivatePolicyView />,
			},
			{
				path: '/help/account/delete',
				element: <HelpAccountDeleteView />,
			},
			{
				path: '*',
				element: <Navigate to='/' replace />,
			},
		],
	},
])

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
	<ThemeProvider defaultTheme='system' storageKey='ui-theme'>
		<React.StrictMode>
			<AppProvider>
				<TooltipProvider>
					<RouterProvider router={router} />
				</TooltipProvider>
			</AppProvider>
		</React.StrictMode>
	</ThemeProvider>,
)

declare global {
	interface BigInt {
		toJSON(): number
	}
}

BigInt.prototype.toJSON = function () {
	return Number(this)
}
